import { useCallback, useMemo, useState } from 'react';
import { usePlanEntitlements } from '../../providers/PlanEntitlementsProvider';
import { supabase } from '../../../shared/supabase/supabaseClient';
import {
  parseExpiredSignupGrant,
  parseLapsedSubscription,
  subscriptionLapsedPromptCopy,
  trialExpiredPromptCopy,
} from '../../../../../../shared/billing-catalog';
import { isBillingEnabled } from '../isBillingEnabled';
import type { PaidPlanCode } from '../../feature-flags/planIntentService';

export function usePlanLapsedPrompts(onChoosePlan: (planCode: PaidPlanCode) => void) {
  const { entitlements, refresh } = usePlanEntitlements();
  const [trialBusy, setTrialBusy] = useState(false);
  const [subscriptionBusy, setSubscriptionBusy] = useState(false);
  // billing disabled — free mode: suppress lapsed/trial modals (keep IAP/MP subscription code)
  const billingOn = isBillingEnabled();

  const expiredGrant = useMemo(
    () => parseExpiredSignupGrant(entitlements?.expired_signup_grant),
    [entitlements?.expired_signup_grant],
  );

  const lapsedSubscription = useMemo(
    () => parseLapsedSubscription(entitlements?.lapsed_subscription),
    [entitlements?.lapsed_subscription],
  );

  const trialVisible =
    billingOn &&
    entitlements?.is_owner === true &&
    entitlements?.show_signup_trial_expired_prompt === true;

  const subscriptionVisible =
    billingOn &&
    entitlements?.is_owner === true &&
    entitlements?.show_subscription_lapsed_prompt === true;

  const trialCopy = useMemo(() => trialExpiredPromptCopy(expiredGrant), [expiredGrant]);
  const subscriptionCopy = useMemo(
    () => subscriptionLapsedPromptCopy(lapsedSubscription),
    [lapsedSubscription],
  );

  const acknowledgeTrial = useCallback(async () => {
    if (trialBusy) return false;
    setTrialBusy(true);
    try {
      const { error } = await supabase.rpc('acknowledge_signup_trial_expiry');
      if (error) {
        console.warn('[planLapsedPrompts] acknowledge signup trial', error.message);
        return false;
      }
      await refresh({ force: true });
      return true;
    } finally {
      setTrialBusy(false);
    }
  }, [trialBusy, refresh]);

  const acknowledgeSubscription = useCallback(async () => {
    if (subscriptionBusy) return false;
    setSubscriptionBusy(true);
    try {
      const { error } = await supabase.rpc('acknowledge_subscription_lapsed');
      if (error) {
        console.warn('[planLapsedPrompts] acknowledge subscription lapsed', error.message);
        return false;
      }
      await refresh({ force: true });
      return true;
    } finally {
      setSubscriptionBusy(false);
    }
  }, [subscriptionBusy, refresh]);

  const makePromptHandlers = useCallback(
    (acknowledge: () => Promise<boolean>, planCode: PaidPlanCode) => ({
      dismiss: async () => {
        await acknowledge();
      },
      choosePlan: async () => {
        const ok = await acknowledge();
        if (ok) onChoosePlan(planCode);
      },
    }),
    [onChoosePlan],
  );

  const trialHandlers = makePromptHandlers(acknowledgeTrial, 'crece');
  const subscriptionHandlers = makePromptHandlers(
    acknowledgeSubscription,
    lapsedSubscription?.plan_code === 'emprende' ? 'emprende' : 'crece',
  );

  return {
    trialExpired: {
      visible: trialVisible && !subscriptionVisible,
      copy: trialCopy,
      busy: trialBusy,
      expiredGrant,
      ...trialHandlers,
    },
    subscriptionLapsed: {
      visible: subscriptionVisible,
      copy: subscriptionCopy,
      busy: subscriptionBusy,
      lapsedSubscription,
      ...subscriptionHandlers,
    },
  };
}
